import React, { useCallback, useEffect, useState } from 'react';
import { Row, Col, Label, Button } from 'reactstrap';
import Swal from 'sweetalert2';
import DataTable from 'react-data-table-component';
import DataTableExtensions from 'react-data-table-component-extensions';
import DateRangePicker from '@wojtekmaj/react-daterange-picker';
import { Translate } from '../../../contexts/Language';
import { BeneficiariesTable } from './BeneficiariesTable';
import { PaymentMethodsTable } from './PaymentMethodsTable';
import { Config } from '../../../config';
import { Service } from '../../../services';
import { CreatePaymentModal } from './CreatePaymentModal';
import { PaymentMethodFormModal } from './PaymentMethodFormModal';
import { BeneficiaryFormModal } from './BeneficiaryFormModal';
import { WithdrawalDetailModal } from './WithdrawalDetailModal';


const getStorageDate = key => {
  const stored = localStorage.getItem(key);
  return stored ? new Date(stored) : new Date();
};

export const WithdrawalTab = ({ selectedAccount, accountData }) => {
  // Withdrawal State
  const [withdrawalFormData, setWithdrawalFormData] = useState({
    start: getStorageDate('uniPaymentBeginDate'),
    end: getStorageDate('uniPaymentEndDate'),
    page_size: 10,
    page_no: 1,
    is_asc: false,
  });
  const [withdrawalDataTable, setWithdrawalDataTable] = useState({
    rows: [],
    totalRows: 0,
  });
  const [withdrawalDetailModal, setWithdrawalDetailModal] = useState(false);
  const [withdrawalDetail, setWithdrawalDetail] = useState({});
  const [loading, setLoading] = useState(false);
  // Beneficiary Management
  const [beneficiaries, setBeneficiaries] = useState([]);
  const [beneficiaryModal, setBeneficiaryModal] = useState(false);
  const [beneficiaryFormData, setBeneficiaryFormData] = useState({
    email: '',
    name: '',
    address: '',
    city: '',
    state: '',
    country: '',
    zipcode: '',
    phone: '',
    type: '',
    relationship: '',
  });
  const [editingBeneficiaryId, setEditingBeneficiaryId] = useState(null);

  // Payment Method Management
  const [paymentMethods, setPaymentMethods] = useState([]);
  const [paymentMethodModal, setPaymentMethodModal] = useState(false);
  const [paymentMethodFormData, setPaymentMethodFormData] = useState({
    beneficiary_id: '',
    title: '',
    transfer_method: 'BANK',
    // BANK fields
    network: '',
    asset_type: '',
    account_number: '',
    iban: '',
    bic: '',
    routing_number: '',
    bank_identifier: '',
    reference: '',
    bank_name: '',
    bank_address: '',
    bank_country: '',
    intermediary_bank_name: '',
    intermediary_account_number: '',
    intermediary_bic: '',
    // CRYPTO fields
    address: '',
    // INTERNAL fields
    uid: '',
  });
  const [editingPaymentMethodId, setEditingPaymentMethodId] = useState(null);

  // Create Payment Modal
  const [createPaymentModal, setCreatePaymentModal] = useState(false);
  const [createPaymentFormData, setCreatePaymentFormData] = useState({
    from_account_id: '',
    beneficiary_id: '',
    payment_method_id: '',
    to_account_id: '',
    asset_type: '',
    amount: '',
    reason: '',
    reference: '',
    note: '',
    unique_id: '',
  });

  const [confirmModal, setConfirmModal] = useState(false);
  const [confirmAction, setConfirmAction] = useState(null);
  const [confirmMessage, setConfirmMessage] = useState('');

  const fetchWithdrawals = useCallback(() => {
    Config.utilities.loadingFetch();
    const formData = { ...withdrawalFormData };
    setLoading(true);

    Service.UniPayment.QueryPayments(formData).then(response => {
      if (response) {
        setWithdrawalDataTable({ rows: response });
      }
      Config.utilities.loadingFetch();
      setLoading(false);
    });
  }, [withdrawalFormData]);

  const fetchBeneficiaries = useCallback(() => {
    Service.UniPayment.QueryBeneficiaries({}).then(response => {
      if (response && response.error === false) {
        setBeneficiaries(response.data.data.models);
      }
    });
  }, []);

  // const fetchPaymentMethods = useCallback(() => {
  //   Service.UniPayment.QueryPaymentMethods({}).then(response => {
  //     if (response && response.error === false) {
  //       setPaymentMethods(response.data.data.models || []);
  //     }
  //   });
  // }, []);

  // Component mount - useEffect replaces componentDidMount
  // useEffect(() => {
  //     fetchDeposits();
  //     fetchBeneficiaries();
  //     fetchPaymentMethods();
  // }, []); // Empty dependency array means this runs once on mount

  // Tab change effect
  useEffect(() => {
    fetchWithdrawals();
    fetchBeneficiaries();
    // fetchPaymentMethods();
  }, []); // Re-run when activeTab changes

  const onWithdrawalDateChange = useCallback(date => {
    if (date !== null && Array.isArray(date)) {
      setWithdrawalFormData(prev => ({
        ...prev,
        start: date[0],
        end: date[1],
      }));
    } else {
      setWithdrawalFormData(prev => ({
        ...prev,
        start: '',
        end: '',
      }));
    }
  }, []);

  const onWithdrawalDetail = useCallback(withdrawal => {
    Config.utilities.loadingFetch();
    Service.UniPayment.GetPayment(withdrawal.id).then(response => {
      if (response) {
        setWithdrawalDetail(response);
        setWithdrawalDetailModal(true);
      }
      Config.utilities.loadingFetch();
    });
  }, []);

  const onCloseModal = useCallback(() => {
    setWithdrawalDetailModal(false);
    setBeneficiaryModal(false);
    setPaymentMethodModal(false);
    setCreatePaymentModal(false);
    setConfirmModal(false);
    setEditingBeneficiaryId(null);
    setEditingPaymentMethodId(null);
    setBeneficiaryFormData({
      email: '',
      name: '',
      address: '',
      city: '',
      state: '',
      country: '',
      postalCode: '',
      phoneNumber: '',
    });
    setPaymentMethodFormData({
      beneficiary_id: '',
      title: '',
      transfer_method: 'BANK',
      network: '',
      asset_type: '',
      account_number: '',
      iban: '',
      bic: '',
      routing_number: '',
      bank_identifier: '',
      reference: '',
      bank_name: '',
      bank_address: '',
      bank_country: '',
      intermediary_bank_name: '',
      intermediary_account_number: '',
      intermediary_bic: '',
      address: '',
      uid: '',
    });
    setCreatePaymentFormData({
      from_account_id: '',
      beneficiary_id: '',
      payment_method_id: '',
      to_account_id: '',
      asset_type: '',
      amount: '',
      reason: '',
      reference: '',
      note: '',
      unique_id: '',
    });
  }, []);

  const onOpenBeneficiaryModal = useCallback((beneficiary = null) => {
    if (beneficiary) {
      setBeneficiaryModal(true);
      setEditingBeneficiaryId(beneficiary.id);
      setBeneficiaryFormData({ ...beneficiary });
    } else {
      setBeneficiaryModal(true);
      setEditingBeneficiaryId(null);
      setBeneficiaryFormData({
        email: '',
        name: '',
        address: '',
        city: '',
        state: '',
        country: '',
        postalCode: '',
        phoneNumber: '',
      });
    }
  }, []);

  const onSaveBeneficiary = useCallback(() => {
    Config.utilities.loadingFetch();

    const promise = editingBeneficiaryId
      ? Service.UniPayment.UpdateBeneficiary(editingBeneficiaryId, beneficiaryFormData)
      : Service.UniPayment.CreateBeneficiary(beneficiaryFormData);

    promise.then(response => {
      Config.utilities.loadingFetch();
      if (response && response.error === false) {
        fetchBeneficiaries();
        onCloseModal();
        Swal.fire({
          icon: 'success',
          title: Translate('Success'),
          text: editingBeneficiaryId
            ? Translate('Beneficiary updated successfully')
            : Translate('Beneficiary created successfully'),
          timer: 2000,
          showConfirmButton: false,
        });
      } else {
        Swal.fire({
          icon: 'error',
          title: Translate('Error'),
          text: response.message || Translate('An error occurred while saving the beneficiary.'),
        });
      }
    }).catch(error => {
      Config.utilities.loadingFetch();
      Swal.fire({
        icon: 'error',
        title: Translate('Error'),
        text: Translate('Failed to save beneficiary. Please try again.'),
      });
    });
  }, [editingBeneficiaryId, beneficiaryFormData, fetchBeneficiaries, onCloseModal]);

  const onEditBeneficiary = useCallback(beneficiary => {
    onOpenBeneficiaryModal(beneficiary);
  }, [onOpenBeneficiaryModal]);

  const onDeleteBeneficiary = useCallback(beneficiaryId => {
    setConfirmModal(true);
    setConfirmAction(() => () => {
      Config.utilities.loadingFetch();
      Service.UniPayment.DeleteBeneficiary(beneficiaryId).then(response => {
        Config.utilities.loadingFetch();
        if (response && response.error === false) {
          fetchBeneficiaries();
          onCloseModal();
          Swal.fire({
            icon: 'success',
            title: Translate('Success'),
            text: Translate('Beneficiary deleted successfully'),
            timer: 2000,
            showConfirmButton: false,
          });
        } else {
          Swal.fire({
            icon: 'error',
            title: Translate('Error'),
            text: response.message || Translate('Failed to delete beneficiary'),
          });
        }
      }).catch(error => {
        Config.utilities.loadingFetch();
        Swal.fire({
          icon: 'error',
          title: Translate('Error'),
          text: Translate('Failed to delete beneficiary. Please try again.'),
        });
      });
    });
    setConfirmMessage(Translate('Are you sure you want to delete this beneficiary?'));
  }, [fetchBeneficiaries, onCloseModal]);

  const onOpenPaymentMethodModal = useCallback((paymentMethod = null) => {
    if (paymentMethod) {
      setPaymentMethodModal(true);
      setEditingPaymentMethodId(paymentMethod.id);
      // Parse detail JSON if it's a string
      const detail = typeof paymentMethod.detail === 'string'
        ? JSON.parse(paymentMethod.detail)
        : paymentMethod.detail || {};
      setPaymentMethodFormData({
        beneficiary_id: paymentMethod.beneficiary_id,
        title: paymentMethod.title,
        transfer_method: paymentMethod.transfer_method,
        network: detail.network || '',
        asset_type: detail.asset_type || '',
        account_number: detail.account_number || '',
        iban: detail.iban || '',
        bic: detail.bic || '',
        routing_number: detail.routing_number || '',
        bank_identifier: detail.bank_identifier || '',
        reference: detail.reference || '',
        bank_name: detail.bank_name || '',
        bank_address: detail.bank_address || '',
        bank_country: detail.bank_country || '',
        intermediary_bank_name: detail.intermediary_bank_name || '',
        intermediary_account_number: detail.intermediary_account_number || '',
        intermediary_bic: detail.intermediary_bic || '',
        address: detail.address || '',
        uid: detail.uid || '',
      });
    } else {
      setPaymentMethodModal(true);
      setEditingPaymentMethodId(null);
      setPaymentMethodFormData({
        beneficiary_id: '',
        title: '',
        transfer_method: 'BANK',
        network: '',
        asset_type: '',
        account_number: '',
        iban: '',
        bic: '',
        routing_number: '',
        bank_identifier: '',
        reference: '',
        bank_name: '',
        bank_address: '',
        bank_country: '',
        intermediary_bank_name: '',
        intermediary_account_number: '',
        intermediary_bic: '',
        address: '',
        uid: '',
      });
    }
  }, []);
  const onSavePaymentMethod = useCallback(() => {
    Config.utilities.loadingFetch();

    // Build detail object based on transfer method
    const { transfer_method, beneficiary_id, title } = paymentMethodFormData;
    let detail = {};

    if (transfer_method === 'BANK') {
      detail = {
        network: paymentMethodFormData.network,
        asset_type: paymentMethodFormData.asset_type,
        account_number: paymentMethodFormData.account_number,
        iban: paymentMethodFormData.iban,
        bic: paymentMethodFormData.bic,
        routing_number: paymentMethodFormData.routing_number,
        bank_identifier: paymentMethodFormData.bank_identifier,
        reference: paymentMethodFormData.reference,
        bank_name: paymentMethodFormData.bank_name,
        bank_address: paymentMethodFormData.bank_address,
        bank_country: paymentMethodFormData.bank_country,
        intermediary_bank_name: paymentMethodFormData.intermediary_bank_name,
        intermediary_account_number: paymentMethodFormData.intermediary_account_number,
        intermediary_bic: paymentMethodFormData.intermediary_bic,
      };
    } else if (transfer_method === 'CRYPTO') {
      detail = {
        network: paymentMethodFormData.network,
        asset_type: paymentMethodFormData.asset_type,
        address: paymentMethodFormData.address,
      };
    } else if (transfer_method === 'INTERNAL') {
      detail = {
        asset_type: paymentMethodFormData.asset_type,
        uid: paymentMethodFormData.uid,
      };
    }

    const payload = {
      beneficiary_id,
      title,
      transfer_method,
      detail,
    };

    const promise = editingPaymentMethodId
      ? Service.UniPayment.UpdatePaymentMethod(editingPaymentMethodId, payload)
      : Service.UniPayment.CreatePaymentMethod(payload);

    promise.then(response => {
      Config.utilities.loadingFetch();
      if (response && response.error === false) {
        // fetchPaymentMethods();
        onCloseModal();
        Swal.fire({
          icon: 'success',
          title: Translate('Success'),
          text: editingPaymentMethodId
            ? Translate('Payment method updated successfully')
            : Translate('Payment method created successfully'),
          timer: 2000,
          showConfirmButton: false,
        });
      } else {
        Swal.fire({
          icon: 'error',
          title: Translate('Error'),
          text: response.message || Translate('An error occurred while saving the payment method.'),
        });
      }
    }).catch(error => {
      Config.utilities.loadingFetch();
      Swal.fire({
        icon: 'error',
        title: Translate('Error'),
        text: Translate('Failed to save payment method. Please try again.'),
      });
    });
  }, [editingPaymentMethodId, paymentMethodFormData, onCloseModal]);

  // const onEditPaymentMethod = useCallback((paymentMethod) => {
  //   onOpenPaymentMethodModal(paymentMethod);
  // }, [onOpenPaymentMethodModal]);

  // const onDeletePaymentMethod = useCallback((paymentMethodId) => {
  //   setConfirmModal(true);
  //   setConfirmAction(() => () => {
  //     Config.utilities.loadingFetch();
  //     Service.UniPayment.DeletePaymentMethod(paymentMethodId).then(response => {
  //       if (response) {
  //         fetchPaymentMethods();
  //         onCloseModal();
  //       }
  //       Config.utilities.loadingFetch();
  //     });
  //   });
  //   setConfirmMessage(Translate('Are you sure you want to delete this payment method?'));
  // }, [fetchPaymentMethods, onCloseModal]);

  const onOpenCreatePaymentModal = useCallback(() => {
    if (!selectedAccount) {
      Swal.fire({
        icon: 'warning',
        title: Translate('Warning'),
        text: Translate('Please select an account first'),
      });
      return;
    }
    setCreatePaymentModal(true);
    setCreatePaymentFormData({
      from_account_id: selectedAccount,
      beneficiary_id: '',
      payment_method_id: '',
      to_account_id: '',
      asset_type: '',
      amount: '',
      reason: '',
      reference: '',
      note: '',
      unique_id: '',
    });
  }, [selectedAccount]);

  const onCreatePayment = useCallback(() => {
    // Validate required fields
    if (!createPaymentFormData.from_account_id || !createPaymentFormData.asset_type ||
      !createPaymentFormData.amount || !createPaymentFormData.reason) {
      Swal.fire({
        icon: 'warning',
        title: Translate('Validation Error'),
        text: Translate('Please fill all required fields'),
      });
      return;
    }

    // Validate either payment_method_id or to_account_id is provided
    if (!createPaymentFormData.payment_method_id && !createPaymentFormData.to_account_id) {
      Swal.fire({
        icon: 'warning',
        title: Translate('Validation Error'),
        text: Translate('Please select a payment method or provide a destination account ID'),
      });
      return;
    }

    // Show confirmation dialog
    Swal.fire({
      title: Translate('Confirm Payment'),
      html: `
        <div style="text-align: left;">
          <p><strong>${Translate('Amount')}:</strong> ${createPaymentFormData.amount} ${createPaymentFormData.asset_type}</p>
          <p><strong>${Translate('Reason')}:</strong> ${createPaymentFormData.reason}</p>
          ${createPaymentFormData.note ? `<p><strong>${Translate('Note')}:</strong> ${createPaymentFormData.note}</p>` : ''}
        </div>
      `,
      icon: 'question',
      showCancelButton: true,
      confirmButtonText: Translate('Yes, Create'),
      cancelButtonText: Translate('Cancel'),
    }).then(result => {
      if (result.isConfirmed) {
        Config.utilities.loadingFetch();
        // Build payload
        const payload = {
          from_account_id: createPaymentFormData.from_account_id,
          asset_type: createPaymentFormData.asset_type,
          amount: parseFloat(createPaymentFormData.amount),
          reason: createPaymentFormData.reason,
        };

        if (createPaymentFormData.payment_method_id) {
          payload.payment_method_id = createPaymentFormData.payment_method_id;
        }
        if (createPaymentFormData.to_account_id) {
          payload.to_account_id = createPaymentFormData.to_account_id;
        }
        if (createPaymentFormData.reference) {
          payload.reference = createPaymentFormData.reference;
        }
        if (createPaymentFormData.note) {
          payload.note = createPaymentFormData.note;
        }
        if (createPaymentFormData.unique_id) {
          payload.unique_id = createPaymentFormData.unique_id;
        }

        Service.UniPayment.CreatePayment(payload).then(response => {
          Config.utilities.loadingFetch();
          if (response && response.error === false) {
            fetchWithdrawals();
            onCloseModal();
            Swal.fire({
              icon: 'success',
              title: Translate('Success'),
              text: Translate('Payment created successfully and is awaiting approval'),
              timer: 3000,
              showConfirmButton: false,
            });
          } else {
            Swal.fire({
              icon: 'error',
              title: Translate('Error'),
              text: response.message || Translate('Failed to create payment'),
            });
          }
        }).catch(error => {
          Config.utilities.loadingFetch();
          Swal.fire({
            icon: 'error',
            title: Translate('Error'),
            text: Translate('Failed to create payment. Please try again.'),
          });
        });
      }
    });
  }, [createPaymentFormData, fetchWithdrawals, onCloseModal]);

  const onConfirmPayment = useCallback((paymentId, payment) => {
    Swal.fire({
      title: Translate('Approve Payment'),
      html: `
        <div style="text-align: left;">
          <p>${Translate('Are you sure you want to approve this payment?')}</p>
          <p><strong>${Translate('Amount')}:</strong> ${payment.amount} ${payment.currency || payment.asset_type}</p>
          <p><strong>${Translate('Status')}:</strong> ${payment.status}</p>
        </div>
      `,
      icon: 'question',
      showCancelButton: true,
      confirmButtonColor: '#28a745',
      cancelButtonColor: '#6c757d',
      confirmButtonText: Translate('Yes, Approve'),
      cancelButtonText: Translate('Cancel'),
    }).then(result => {
      if (result.isConfirmed) {
        Config.utilities.loadingFetch();
        Service.UniPayment.ConfirmPayment(paymentId).then(response => {
          Config.utilities.loadingFetch();
          if (response && response.error === false) {
            fetchWithdrawals();
            Swal.fire({
              icon: 'success',
              title: Translate('Success'),
              text: Translate('Payment approved successfully'),
              timer: 2000,
              showConfirmButton: false,
            });
          } else {
            Swal.fire({
              icon: 'error',
              title: Translate('Error'),
              text: response.message || Translate('Failed to approve payment'),
            });
          }
        }).catch(error => {
          Config.utilities.loadingFetch();
          Swal.fire({
            icon: 'error',
            title: Translate('Error'),
            text: Translate('Failed to approve payment. Please try again.'),
          });
        });
      }
    });
  }, [fetchWithdrawals]);

  const onCancelPayment = useCallback((paymentId, payment) => {
    Swal.fire({
      title: Translate('Reject Payment'),
      html: `
        <div style="text-align: left;">
          <p>${Translate('Are you sure you want to reject this payment?')}</p>
          <p><strong>${Translate('Amount')}:</strong> ${payment.amount} ${payment.currency || payment.asset_type}</p>
          <p><strong>${Translate('Status')}:</strong> ${payment.status}</p>
        </div>
      `,
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#dc3545',
      cancelButtonColor: '#6c757d',
      confirmButtonText: Translate('Yes, Reject'),
      cancelButtonText: Translate('Cancel'),
    }).then(result => {
      if (result.isConfirmed) {
        Config.utilities.loadingFetch();
        Service.UniPayment.CancelPayment(paymentId).then(response => {
          Config.utilities.loadingFetch();
          if (response && response.error === false) {
            fetchWithdrawals();
            Swal.fire({
              icon: 'success',
              title: Translate('Success'),
              text: Translate('Payment rejected successfully'),
              timer: 2000,
              showConfirmButton: false,
            });
          } else {
            Swal.fire({
              icon: 'error',
              title: Translate('Error'),
              text: response.message || Translate('Failed to reject payment'),
            });
          }
        }).catch(error => {
          Config.utilities.loadingFetch();
          Swal.fire({
            icon: 'error',
            title: Translate('Error'),
            text: Translate('Failed to reject payment. Please try again.'),
          });
        });
      }
    });
  }, [fetchWithdrawals]);

  const updateBeneficiaryField = useCallback((field, value) => {
    setBeneficiaryFormData(prev => ({
      ...prev,
      [field]: value,
    }));
  }, []);

  const updatePaymentMethodField = useCallback((field, value) => {
    setPaymentMethodFormData(prev => ({
      ...prev,
      [field]: value,
    }));
  }, []);

  const updateCreatePaymentField = useCallback((field, value) => {
    setCreatePaymentFormData(prev => ({
      ...prev,
      [field]: value,
    }));
  }, []);

  const columns = [
    {
      name: Translate('ID'),
      selector: 'id',
      sortable: true,
      width: '100px',
      cell: d => (
        <b>
#
          {d.id}
        </b>
      ),
    },
    {
      name: Translate('Date'),
      selector: 'createdAt',
      sortable: true,
      width: '150px',
    },
    {
      name: Translate('Amount'),
      selector: 'amount',
      sortable: true,
      width: '120px',
      cell: d => (
        <span className="badge badge-danger">
          {d.amount}
          {' '}
          {d.currency}
        </span>
      ),
    },
    {
      name: Translate('Status'),
      selector: 'status',
      width: '120px',
      cell: d => {
        const statusColors = {
          completed: 'success',
          pending: 'warning',
          failed: 'danger',
          processing: 'info',
          confirmed: 'success',
          cancelled: 'secondary',
        };
        return <span className={`badge badge-${statusColors[d.status] || 'secondary'}`}>{d.status}</span>;
      },
    },
    {
      name: Translate('Beneficiary'),
      selector: 'beneficiaryName',
      width: '150px',
    },
    {
      name: Translate('Actions'),
      cell: d => (
        <div>
          <Button color="info" size="sm" onClick={() => onWithdrawalDetail(d)} className="mr-1">
            {Translate('Details')}
          </Button>
          {(d.status === 'Pending' || d.status === 'pending') && (
            <>
              <Button color="success" size="sm" onClick={() => onConfirmPayment(d.id, d)} className="mr-1">
                {Translate('Approve')}
              </Button>
              <Button color="danger" size="sm" onClick={() => onCancelPayment(d.id, d)}>
                {Translate('Reject')}
              </Button>
            </>
          )}
        </div>
      ),
      ignoreRowClick: true,
      allowOverflow: true,
      button: true,
      width: '250px',
    },
  ];

  return (
    <div className="p-3">
      <Row className="mb-3">
        <Col md={12}>
          <h5>{Translate('Withdrawal Settings')}</h5>
        </Col>
      </Row>

      <BeneficiariesTable
        beneficiaries={beneficiaries}
        onAdd={onOpenBeneficiaryModal}
        onEdit={onEditBeneficiary}
        onDelete={onDeleteBeneficiary}
      />

      <Row className="mb-4">
        <Col md={12}>
          <div className="d-flex justify-content-between align-items-center mb-2">
            <h6>{Translate('Payment Methods')}</h6>
            <Button color="success" size="sm" onClick={() => onOpenPaymentMethodModal()}>
              {Translate('Add Payment Method')}
            </Button>
          </div>
        </Col>
      </Row>
      {/* <PaymentMethodsTable
        paymentMethods={paymentMethods}
        onAdd={onOpenPaymentMethodModal}
        onEdit={onEditPaymentMethod}
        onDelete={onDeletePaymentMethod}
        progressPending={loading}
        pagination
        paginationServer
        paginationTotalRows={withdrawalDataTable.totalRows}
        onChangeRowsPerPage={handlePerRowsChange}
        onChangePage={handlePageChange}
      /> */}

      <hr />

      <Row className="mt-4">
        <Col md={12}>
          <div className="d-flex justify-content-between align-items-center mb-3">
            <h5>{Translate('Awaiting Payments')}</h5>
            <Button color="primary" onClick={onOpenCreatePaymentModal}>
              {Translate('Create Payment')}
            </Button>
          </div>
          <p className="text-muted">
            {Translate('Payments with Pending status are awaiting approval. You can approve or reject them.')}
          </p>
        </Col>
      </Row>

      <Row className="mb-3">
        <Col md={6}>
          <Label>{Translate('Date Range')}</Label>
          <DateRangePicker
            onChange={onWithdrawalDateChange}
            value={[withdrawalFormData.startDate, withdrawalFormData.end]}
            className="form-control"
          />
        </Col>
        <Col md={6} className="d-flex align-items-end">
          <Button color="primary" onClick={fetchWithdrawals}>
            {Translate('Filter')}
          </Button>
        </Col>
      </Row>

      <DataTableExtensions
        columns={columns}
        data={withdrawalDataTable.rows}
        export
        print={false}
      >
        <DataTable
          columns={columns}
          data={withdrawalDataTable.rows}
          pagination
          highlightOnHover
          striped
        />
      </DataTableExtensions>
      <BeneficiaryFormModal
        show={beneficiaryModal}
        isEditing={editingBeneficiaryId !== null}
        formData={beneficiaryFormData}
        onChange={updateBeneficiaryField}
        onSave={onSaveBeneficiary}
        onClose={onCloseModal}
      />

      <PaymentMethodFormModal
        show={paymentMethodModal}
        isEditing={editingPaymentMethodId !== null}
        formData={paymentMethodFormData}
        beneficiaries={beneficiaries}
        onChange={updatePaymentMethodField}
        onSave={onSavePaymentMethod}
        onClose={onCloseModal}
      />

      <CreatePaymentModal
        show={createPaymentModal}
        formData={createPaymentFormData}
        beneficiaries={beneficiaries}
        selectedAccount={selectedAccount}
        onChange={updateCreatePaymentField}
        onCreate={onCreatePayment}
        onClose={onCloseModal}
        accountData={accountData}
      />
      <WithdrawalDetailModal
        show={withdrawalDetailModal}
        detail={withdrawalDetail}
        onClose={onCloseModal}
      />
      {/* <Confirm
                Show={confirmModal}
                Title={Translate('Confirm Action')}
                Message={confirmMessage}
                onConfirm={() => {
                    if (confirmAction) confirmAction();
                }}
                onCancel={onCloseModal}
            /> */}
    </div>
  );
};
